import { Component } from 'react'

import { IField } from '@features/forms'

import { getValues } from './utils'
import { IProps } from './types'

interface IState {
  fields: { [key: string]: IField }
}

interface IFormProps {
  initialState: { [key: string]: IField }
}

interface Form<P = {}> {}

abstract class Form<P> extends Component<IProps & IFormProps & P, IState> {
  constructor(props: IProps & IFormProps & P) {
    super(props)
    this.state = {
      fields: props.initialState
    }
    this.validate = props.validate || this.validate.bind(this)
    this.handleFieldFocus = props.onFocus || this.handleFieldFocus.bind(this)
    this.handleFieldChange = props.onChange || this.handleFieldChange.bind(this)
    this.handleFieldBlur = props.onBlur || this.handleFieldBlur.bind(this)
    this.handleErrors = this.handleErrors.bind(this)
    this.handleSubmit = this.handleSubmit.bind(this)
  }

  validate(_: any): any {
    return null
  }

  handleFieldFocus(name: string) {
    this.setState({
      fields: {
        ...this.state.fields,
        [name]: {
          ...this.state.fields[name],
          focussed: true
        }
      }
    })
  }

  handleFieldChange(name: string, value: any): Promise<any> {
    return new Promise((res) => {
      return this.setState(
        {
          fields: {
            ...this.state.fields,
            [name]: {
              ...this.state.fields[name],
              value
            }
          }
        },
        res
      )
    })
  }

  handleFieldBlur(name: string) {
    this.handleErrors()
    this.setState({
      fields: {
        ...this.state.fields,
        [name]: {
          ...this.state.fields[name],
          focussed: false,
          touched: true
        }
      }
    })
  }

  touchFields(fieldNames: string[]) {
    const { fields } = this.state
    for (const fieldName of fieldNames) {
      fields[fieldName].touched = true
    }
    this.setState({ fields })
    return fields
  }

  updateFieldErrors(fields: { [key: string]: IField }, errors: any) {
    // Blank existing errors
    for (const field of Object.values(fields)) {
      fields[field.name].error = null
    }

    // Add new errors
    for (const fieldName in errors) {
      if (Object.prototype.hasOwnProperty.call(errors, fieldName)) {
        if (fields[fieldName]) {
          fields[fieldName].error = errors[fieldName]
        }
      }
    }

    return fields
  }

  handleErrors() {
    const errors = this.validate(getValues(Object.values(this.state.fields)))
    this.setState({
      fields: this.updateFieldErrors(this.state.fields, errors || {})
    })
    return errors
  }

  touchAllFields = () => {
    let { fields } = this.state

    for (const field of Object.values(fields)) {
      fields[field.name].touched = true
    }

    this.setState({
      fields: fields
    })
  }

  handleSubmit(e: any) {
    e.preventDefault()
    const { fields } = this.state
    const fieldValues = getValues(Object.values(fields))
    const errors = this.handleErrors()
    if (errors) {
      this.touchAllFields()
      if (this.props.onError) {
        this.props.onError(fieldValues, errors)
      }
    } else {
      this.props.onSubmit(fieldValues)
    }

    return false
  }

  /**
   * getValues
   * Get the dict of field values mapped against their field names.
   */
  getValues = () => {
    return getValues(Object.values(this.state.fields))
  }
}

export default Form
